import { captureException } from '@sentry/react'
import { get, memoize } from 'lodash-es'
import React, { FunctionComponent, PropsWithChildren, useContext, useEffect, useState } from 'react'
import type { Primitive, SetRequired, SetReturnType } from 'type-fest'
import type { DeepPaths, Get, ShopifyLanguage, ShopifyLocale } from '~/types'
import { objectMap } from '~/utils'
import English from '../../../locales/en.default.json'
import { parse } from './parser'

type TranslationSchema<T> = T extends Record<string, Primitive>
	? { [K in keyof T]?: TranslationSchema<T[K]> }
	: T extends Record<string, any>
	? { [K in keyof T]: TranslationSchema<T[K]> }
	: T extends `${string}/>${string}` // TODO: HTML element detection pending https://github.com/microsoft/TypeScript/issues/32063
	? FunctionComponent
	: T

export type Translations = SetRequired<Partial<TranslationSchema<typeof English>>, 'react'>

interface Props extends PropsWithChildren {
	lang?: ShopifyLanguage
}
const defaultLang = window.localization.language
const Language = React.createContext<ShopifyLanguage>(defaultLang)

/**
 * Like React's built-in {@link createPortal}, except that it watches for possible dom updates made outside React
 * @param props
 * @returns
 */
export const LanguageProvider: React.FC<Props> = ({ lang = defaultLang, children }) => (
	<Language.Provider value={lang}>{children}</Language.Provider>
)

export function useLocale() {
	const language = useContext(Language)
	return language.shop_locale
}

export function useLanguage() {
	const language = useContext(Language)
	return language
}

type Path = DeepPaths<Translations>

export function useTranslations<T extends Path>(
	path?: T,
	variables?: Record<string, Primitive>
): Get<Translations, T> | null {
	const [translations, setTranslations] = useState<Get<Translations, T> | null>(null)
	const lang = useLocale().locale
	useEffect(() => {
		async function load() {
			/* Since loadTranslations is memoized,
			 * Only recompile (and use path) when variables are set */
			const result = await loadTranslations<T>(lang, path, variables)
			setTranslations(result)
		}
		load()
	}, [lang, path, variables])
	return translations
}

const cacheKey: SetReturnType<typeof __lt, string> = (l, p, v) => `${l}.${p}.${v && JSON.stringify(v)})`

export const loadTranslations = memoize(__lt, cacheKey)
async function __lt<T extends Path>(locale: ShopifyLocale['locale'], path?: T, variables?: Record<string, Primitive>) {
	const result = await loadLanguage(locale)
	return parseTranslations(result, path, variables)
}

function parseTranslations<T extends Path>(
	translations: Translations,
	path?: T,
	variables?: Record<string, Primitive>
): Get<Translations, T> {
	const level = path?.split('.').length ?? 0
	const obj = path ? get(translations, path) : translations

	switch (level) {
		case 0:
			return objectMap(obj, category =>
				objectMap(category, subCategory => objectMap(subCategory, entry => parse(entry, variables)))
			) as any
		case 1:
			return objectMap(obj, subCategory => objectMap(subCategory, entry => parse(entry, variables))) as any
		case 2:
			return objectMap(obj, entry => parse(entry, variables)) as any
		default:
			return parse(obj, variables) as any
	}
}

export const loadLanguage = memoize(__ll)
async function __ll(locale: ShopifyLocale['locale']): Promise<Translations> {
	try {
		switch (locale.toLocaleLowerCase()) {
			case 'nb':
				return import('../../../locales/nb.json')

			case 'sv':
				return import('../../../locales/sv.json')

			case 'de':
				return import('../../../locales/de.json')

			case 'da':
				return import('../../../locales/da.json')

			case 'en':
			default:
				return English
		}
	} catch (e) {
		captureException(e)
		return English
	}
}
